import React, { useEffect } from "react";
import { BrowserRouter, Route, Routes, Navigate, Outlet } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Navbar, Footer } from "./components";
import { useUserLoggedin } from "./state/state";
import { useStateContext } from "./contexts/ContextProvider";
import { ThemeContext, themes } from "./themes/ThemeContext";
import SignUpTemplate from "./pages/UserAuth/SignUp/template";
import { DashboardController, PasswordReset, Settings, SignIn, AnalyticsDashboard } from "./pages";

const App = () => {
  const { setCurrentColor, setCurrentMode } = useStateContext();
  const userLoggedIn = useUserLoggedin((state) => state.userLoggedIn);

  // Protected route for logged-in users
  function ProtectedRoute() {
    return userLoggedIn ? <Outlet /> : <Navigate to="/sign-in" />;
  }

  // Redirect logged-in users away from auth pages
  function ProtectedRouteLoggedIn() {
    if (!userLoggedIn) {
      return <Outlet />;
    }
    
    // Check user role and redirect accordingly
    const userRole = localStorage.getItem("userRole");
    if (userRole === "admin") {
      return <Navigate to="/analytics" />;
    } else {
      return <Navigate to="/dashboard" />;
    }
  }

  // Set theme preferences on mount
  useEffect(() => {
    const currentThemeColor = localStorage.getItem("colorMode");
    const currentThemeMode = localStorage.getItem("themeMode");
    if (currentThemeColor && currentThemeMode) {
      setCurrentColor(currentThemeColor);
      setCurrentMode(currentThemeMode);
    }
  }, [setCurrentColor, setCurrentMode]);

  // Handle initial redirect for logged-in users
  useEffect(() => {
    if (userLoggedIn) {
      const userRole = localStorage.getItem("userRole");
      const currentPath = window.location.pathname;
      
      
      // If user is on /dashboard but should be on /analytics, redirect
      if (userRole === "admin" && currentPath === "/dashboard") {
        window.location.href = "/analytics";
      }
      // If user is on /analytics but shouldn't be, redirect to /dashboard
      else if (userRole !== "admin" && currentPath === "/analytics") {
        window.location.href = "/dashboard";
      }
    }
  }, [userLoggedIn]);

  return (
    <ThemeContext.Provider value={themes.dark}>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        closeOnClick
        toastStyle={{ zIndex: 99999 }}
      />
      <BrowserRouter>
        <div className="relative w-full">
          {/* Navbar */}
          <div className="fixed md:static bg-main-bg dark:bg-main-dark-bg navbar w-full">
            <Navbar />
          </div>
  
          {/* Main content */}
          <div
            className="flex w-full"
            style={{
              backgroundColor: userLoggedIn ? themes.dark.colors.background.primary : "",
            }}
          >
            <div className="w-full" style={{ width: "100vw" }}>
              <Routes>
                {/* Redirect to appropriate dashboard based on role if logged in, otherwise to sign-in */}
                <Route
                  path="/"
                  element={
                    userLoggedIn ? (
                      (() => {
                        const userRole = localStorage.getItem("userRole");
                        if (userRole === "admin") {
                          return <Navigate to="/analytics" />;
                        } else {
                          return <Navigate to="/dashboard" />;
                        }
                      })()
                    ) : (
                      <Navigate to="/sign-in" />
                    )
                  }
                />
  
                {/* Auth routes (only accessible if not logged in) */}
                <Route element={<ProtectedRouteLoggedIn />}>
                  <Route path="/sign-up" element={<SignUpTemplate />} />
                  <Route path="/sign-in" element={<SignIn />} />
                  <Route path="/reset-password/:email/:token" element={<PasswordReset />} />
                </Route>
  
                {/* Protected routes (only accessible if logged in) */}
                <Route element={<ProtectedRoute />}>
                  <Route path="/dashboard" element={<DashboardController />} />
                  <Route path="/analytics" element={<AnalyticsDashboard />} />
                  <Route path="/settings" element={<Settings />} />
                </Route>
  
                {/* Catch-all route for undefined paths */}
                <Route
                  path="*"
                  element={
                    userLoggedIn ? (
                      (() => {
                        const userRole = localStorage.getItem("userRole");
                        if (userRole === "admin") {
                          return <Navigate to="/analytics" />;
                        } else {
                          return <Navigate to="/dashboard" />;
                        }
                      })()
                    ) : (
                      <Navigate to="/sign-in" />
                    )
                  }
                />
              </Routes>
            </div>
          </div>
  
          {/* Footer */}
          <Footer />
        </div>
      </BrowserRouter>
    </ThemeContext.Provider>
  );
  
};

export default App;